import PortalDialog, {PortalDialogProperties, PortalDialogState} from "../PortalDialog";
import React, {ReactNode} from "react";
import {Dialog} from "primereact/dialog";
import PortalDialogType from "../../PortalDialogType";
import MimeTypes from "../../../document/mimetype/MimeTypes";
import MimeTypeFilter from "../../../document/mimetype/MimeTypeFilter";
import Parameter from "../../parameter/Parameter";
import PortalDocument from "../../../document/PortalDocument";
import {Button} from "primereact/button";
import {WebServiceType, WebServiceTypes} from "@softvision/webpdf-wsclient-9-typescript";
import {InputSwitch} from "primereact/inputswitch";
import {Dropdown} from "primereact/dropdown";
import PortalDocumentManager from "../../../document/PortalDocumentManager";
import DialogDefault from "../../parameter/PortalDialogDefault";
import ContentExtractionDialogFields from "../../field/export/ContentExtractionDialogFields";
import DocumentSettings from "../../../document/DocumentSettings";
import PageSelection, {PAGERANGE_ALL} from "../../../document/PageSelection";
import MimeType from "../../../document/mimetype/MimeType";
import PageRange from "../../../../shared/components/PageRange";
import Message from "../../../../shared/log/Message";
import {MessageType} from "../../../../shared/log/MessageInterface";
import {
	AdminWebserviceStatus,
	BaseToolbox,
	ExtractionFileFormat,
	ExtractionImageFormat,
	ExtractionImages,
	ExtractionWords,
	Functions,
	Information,
	Security,
	Settings,
	SettingsInterface,
	SignatureEditProfile,
	TextExtractionLinks
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import {
	ExtractionMode,
	PortalToolboxExtractionExtraction
} from "../../../../shared/models/PortalToolboxExtractionExtraction";
import PortalDialogSecurity, {Intention} from "../../PortalDialogSecurity";
import Translate from "../../../../shared/translation/Translate";
import Helper from "../../../../shared/components/Helper";
import Info from "../../../../shared/components/Info";
import Icon from "../../../../shared/components/Icon";
import FunctionHelper from "../../../../shared/helper/FunctionHelper";

interface ContentExtractionDialogStatus extends PortalDialogState<PortalToolboxExtractionExtraction> {
	pages: number;
	currentPage: string;
}

export class ContentExtractionDialogDefinition extends PortalDialog<PortalToolboxExtractionExtraction,
	PortalDialogProperties<PortalToolboxExtractionExtraction>, ContentExtractionDialogStatus> {
	constructor(props: any) {
		super(props);

		this.state = {
			...this.state,
			pages: 1,
			currentPage: "1",
			fieldErrors: new Map<ContentExtractionDialogFields, string>()
		};
	}

	public static isEnabled(portalInfo: Information): boolean {
		return FunctionHelper.applyValue(
			portalInfo.webserviceStatus?.webservices?.toolbox?.enabled, AdminWebserviceStatus.getEnabledDefault()
		) && FunctionHelper.applyValue(
			portalInfo.functionsStatus?.extractContent, Functions.getExtractContentDefault()
		);
	}

	public static isActive(
		selectedDocuments: Array<PortalDocument>, portalInfo: Information, signatureEditProfile: SignatureEditProfile
	): boolean {
		let mimeTypes: Array<MimeType> = [];

		for (let selectedDocument of selectedDocuments) {
			mimeTypes.push(selectedDocument.getMimeType());

			if (selectedDocument.isLocked()) {
				return false;
			}

			if (!PortalDialogSecurity.checkRestrictions(
				PortalDialogType.EXTRACT_CONTENT, signatureEditProfile, Intention.reading, selectedDocument
			)) {
				return false;
			}

			if (!selectedDocument.isEditable({checkArchive: false})) {
				return false;
			}
		}

		return this.isEnabled(portalInfo) && selectedDocuments.length === 1 &&
			MimeTypeFilter.checkSelection(mimeTypes, [MimeTypes.PDF]);
	}

	public renderDialog(): ReactNode {
		let classList: Array<string> = ["export-content-dialog"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide}
					header={this.renderHeader()} footer={this.renderFooter()} resizable={false}
					appendTo={document.getElementById("root")}>
				<div className="p-grid">
					{this.renderError()}
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.extract_content.general"} namespace={"help"}
								   markdown={true}/>
					</Helper>
					<Helper {...this.props} type={MessageType.WARNING}>
						<Translate {...this.props} data={"dialog.extract_content.info"} namespace={"help"}
								   markdown={true}/>
					</Helper>
					{this.renderContents()}
				</div>
			</Dialog>
		);
	}

	public renderContents(): ReactNode {
		let parameter: PortalToolboxExtractionExtraction | undefined = this.state.parameter?.data;
		let errors: Map<ContentExtractionDialogFields, string> = this.state.fieldErrors;

		if (!FunctionHelper.exists(parameter)) {
			return null;
		}

		let pages: string | undefined;

		switch (parameter!.mode) {
			case ExtractionMode.IMAGES:
				pages = parameter?.images?.pages;
				break;
			case ExtractionMode.TEXT:
				pages = parameter?.text?.pages;
				break;
			case ExtractionMode.URL:
				pages = parameter?.links?.pages;
				break;
			case ExtractionMode.WORDS:
				pages = parameter?.words?.pages;
				break;
		}

		return (
			<>
				<fieldset className="p-col-12 p-grid">
					<div className="p-col-12 p-grid"
						 hidden={!this.state.fields?.contains(ContentExtractionDialogFields.MODE)}>
						<label className="p-col-12 p-sm-4" htmlFor="control-mode">
							<Translate {...this.props} data="extract_content.type.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8">
							<Dropdown inputId="control-mode"
									  value={FunctionHelper.applyValue(
										  parameter?.mode, PortalToolboxExtractionExtraction.getModeDefault()
									  )}
									  options={[
										  {
											  label: Translate.value(
												  "extract_content.type.text", {ns: "dialog"}
											  ),
											  value: ExtractionMode.TEXT
										  },
										  {
											  label: Translate.value(
												  "extract_content.type.words", {ns: "dialog"}
											  ),
											  value: ExtractionMode.WORDS
										  },
										  {
											  label: Translate.value(
												  "extract_content.type.url", {ns: "dialog"}
											  ),
											  value: ExtractionMode.URL
										  },
										  {
											  label: Translate.value(
												  "extract_content.type.images", {ns: "dialog"}
											  ),
											  value: ExtractionMode.IMAGES
										  }
									  ]}
									  onChange={(e) => this.onChange(
										  ContentExtractionDialogFields.MODE, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
					<div className="p-col-12 p-grid"
						 hidden={
							 !this.state.fields?.contains(ContentExtractionDialogFields.FILE_FORMAT) ||
							 (
								 parameter?.mode !== ExtractionMode.TEXT &&
								 parameter?.mode !== ExtractionMode.WORDS &&
								 parameter?.mode !== ExtractionMode.URL
							 )
						 }>
						<label className="p-col-12 p-sm-4" htmlFor="control-format">
							<Translate {...this.props} data="extract_content.format.label" namespace="dialog"/>
						</label>
						<div className="p-col-12 p-sm-8">
							<Dropdown inputId="control-format"
									  value={
										  (parameter?.mode === ExtractionMode.TEXT ?
											  parameter?.text?.fileFormat : undefined) ||
										  (parameter?.mode === ExtractionMode.WORDS ?
											  parameter?.words?.fileFormat : undefined) ||
										  (parameter?.mode === ExtractionMode.URL ?
											  parameter?.links?.fileFormat : undefined)
									  }
									  options={[
										  {
											  label: Translate.value(
												  "extract_content.format.xml", {ns: "dialog"}
											  ),
											  value: ExtractionFileFormat.Xml
										  },
										  {
											  label: Translate.value(
												  "extract_content.format.json", {ns: "dialog"}
											  ),
											  value: ExtractionFileFormat.Json
										  },
										  {
											  label: Translate.value(
												  "extract_content.format.text", {ns: "dialog"}
											  ),
											  value: ExtractionFileFormat.Text
										  }
									  ]}
									  onChange={(e) => this.onChange(
										  ContentExtractionDialogFields.FILE_FORMAT, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid"
						  hidden={
							  !this.state.fields?.contains(ContentExtractionDialogFields.FROM_TEXT) ||
							  parameter?.mode !== ExtractionMode.URL
						  }>
					<div className="p-col-12 p-grid">
						<label className="p-col-12 p-sm-4" htmlFor="control-from-text"/>
						<div className="p-col-12 p-sm-8 switch-container">
							<InputSwitch inputId="control-from-text"
										 checked={FunctionHelper.applyValue(
											 parameter?.links?.text?.fromText, TextExtractionLinks.getFromTextDefault()
										 )}
										 onChange={(e) => this.onChange(
											 ContentExtractionDialogFields.FROM_TEXT, e.value
										 )}/>
							<label className="switch-label" htmlFor="control-from-text">
								<Translate {...this.props} data="extract_content.url.from_text.label"
										   namespace="dialog"/>
							</label>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid" hidden={
					(
						!this.state.fields?.contains(ContentExtractionDialogFields.REMOVE_PUNCTUATION) &&
						!this.state.fields?.contains(ContentExtractionDialogFields.DELIMIT_AFTER_PUNCTUATION) &&
						!this.state.fields?.contains(ContentExtractionDialogFields.EXTENDED_SEQUENCE_CHARACTERS)
					) ||
					parameter?.mode !== ExtractionMode.WORDS
				}>
					<div className="p-col-12 p-grid" hidden={
						!this.state.fields?.contains(ContentExtractionDialogFields.REMOVE_PUNCTUATION)
					}>
						<label className="p-col-12 p-sm-4" htmlFor="control-remove-punctuation"/>
						<div className="p-col-12 p-sm-8 switch-container">
							<InputSwitch inputId="control-remove-punctuation"
										 checked={FunctionHelper.applyValue(
											 parameter?.words?.removePunctuation,
											 ExtractionWords.getRemovePunctuationDefault()
										 )}
										 onChange={(e) => this.onChange(
											 ContentExtractionDialogFields.REMOVE_PUNCTUATION, e.value
										 )}/>
							<label className="switch-label" htmlFor="control-remove-punctuation">
								<Translate {...this.props} data="extract_content.words.punctuation.remove.label"
										   namespace="dialog"/>
							</label>
						</div>
					</div>
					<div className="p-col-12 p-grid" hidden={
						!this.state.fields?.contains(ContentExtractionDialogFields.DELIMIT_AFTER_PUNCTUATION)
					}>
						<label className="p-col-12 p-sm-4" htmlFor="control-delimiter-after-punctuation"/>
						<div className="p-col-12 p-sm-8 switch-container">
							<InputSwitch inputId="control-delimiter-after-punctuation"
										 checked={FunctionHelper.applyValue(
											 parameter?.words?.delimitAfterPunctuation,
											 ExtractionWords.getDelimitAfterPunctuationDefault()
										 )}
										 onChange={(e) => this.onChange(
											 ContentExtractionDialogFields.DELIMIT_AFTER_PUNCTUATION, e.value
										 )}/>
							<label className="switch-label" htmlFor="control-delimiter-after-punctuation">
								<Translate {...this.props} data="extract_content.words.punctuation.delimit_after.label"
										   namespace="dialog"/>
							</label>
						</div>
					</div>
					<div className="p-col-12 p-grid" hidden={
						!this.state.fields?.contains(ContentExtractionDialogFields.EXTENDED_SEQUENCE_CHARACTERS)
					}>
						<label className="p-col-12 p-sm-4" htmlFor="control-extended-sequence-characters"/>
						<div className="p-col-12 p-sm-8 switch-container">
							<InputSwitch inputId="control-extended-sequence-characters"
										 checked={FunctionHelper.applyValue(
											 parameter?.words?.extendedSequenceCharacters,
											 ExtractionWords.getExtendedSequenceCharactersDefault()
										 )}
										 onChange={(e) => this.onChange(
											 ContentExtractionDialogFields.EXTENDED_SEQUENCE_CHARACTERS, e.value
										 )}/>
							<label className="switch-label" htmlFor="control-extended-sequence-characters">
								<Translate {...this.props}
										   data="extract_content.words.extended_sequence_characters.label"
										   namespace="dialog"/>
							</label>
						</div>
					</div>
				</fieldset>

				<div className="p-col-12 p-grid"
					 hidden={
						 !this.state.fields?.contains(ContentExtractionDialogFields.FILE_FORMAT) ||
						 parameter?.mode !== ExtractionMode.WORDS
					 }>
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.extract_content.words"} namespace={"help"}
								   markdown={true}/>
					</Helper>
				</div>

				<fieldset className="p-col-12 p-grid" hidden={
					!this.state.fields?.contains(ContentExtractionDialogFields.FALLBACK_FORMAT) ||
					parameter?.mode !== ExtractionMode.IMAGES
				}>
					<div className="p-col-12 p-grid">
						<label className="p-col-12 p-sm-4" htmlFor="control-fallback-format">
							<Translate {...this.props} data="extract_content.images.fallback_format.label"
									   namespace="dialog"/>
							<Info {...this.props} data="extract_content.images.fallback_format.description"
								  options={{ns: "dialog"}}/>
						</label>
						<div className="p-col-12 p-sm-8">
							<Dropdown inputId="control-fallback-format"
									  value={FunctionHelper.applyValue(
										  parameter?.images?.fallbackFormat, ExtractionImages.getFallbackFormatDefault()
									  )}
									  options={[
										  {
											  label: Translate.value(
												  "extract_content.images.fallback_format.png",
												  {ns: "dialog"}
											  ),
											  value: ExtractionImageFormat.Png
										  },
										  {
											  label: Translate.value(
												  "extract_content.images.fallback_format.jpeg",
												  {ns: "dialog"}
											  ),
											  value: ExtractionImageFormat.Jpeg
										  }
									  ]}
									  onChange={(e) => this.onChange(
										  ContentExtractionDialogFields.FALLBACK_FORMAT, e.value
									  )}
									  appendTo={document.getElementById("root")}/>
						</div>
					</div>
				</fieldset>

				<fieldset className="p-col-12 p-grid"
						  hidden={!this.state.fields?.contains(ContentExtractionDialogFields.PAGERANGE)}>
					<div className="p-col-12 p-grid">
						<label className="p-col-12 p-sm-4" htmlFor="control-pagerange">
							<Translate {...this.props} data="extract_content.pages.label" namespace="dialog"/>
							<Info {...this.props} data="extract_content.pages.info" options={{ns: "dialog"}}/>
						</label>
						<div className="p-col-12 p-sm-8">
							<PageRange inputId="control-pagerange" value={pages}
									   className={
										   errors.has(ContentExtractionDialogFields.PAGERANGE) ? " p-invalid" : ""
									   }
									   onChange={(e) => this.onChange(
										   ContentExtractionDialogFields.PAGERANGE, e.value
									   )}
									   appendTo={document.getElementById("root")} editable={true}
									   addon={
										   Translate.value("inputs.pagerange.of", {pages: this.state.pages})
									   }
									   tooltip={errors.has(ContentExtractionDialogFields.PAGERANGE) ? Translate.value(
										   errors.get(ContentExtractionDialogFields.PAGERANGE)!, {ns: "message"}
									   ) : ""}
									   options={[
										   {label: Translate.value("inputs.pagerange.all"), value: PAGERANGE_ALL},
										   {
											   label: Translate.value("inputs.pagerange.current"),
											   value: this.state.currentPage
										   }
									   ]}/>
							{this.renderFieldError(errors.has(ContentExtractionDialogFields.PAGERANGE) ?
								Translate.value(
									errors.get(ContentExtractionDialogFields.PAGERANGE)!, {ns: "message"}
								) : ""
							)}
						</div>
					</div>
				</fieldset>
			</>
		);
	}

	protected onChange(field: ContentExtractionDialogFields, value: any): void {
		let parameter: PortalToolboxExtractionExtraction = this.state.parameter!.data;
		let errors: Map<ContentExtractionDialogFields, string> = this.state.fieldErrors;

		switch (field) {
			case ContentExtractionDialogFields.MODE:
				parameter.mode = value;
				break;
			case ContentExtractionDialogFields.FILE_FORMAT:
				parameter.text!.fileFormat = value;
				parameter.words!.fileFormat = value;
				parameter.links!.fileFormat = value;
				break;
			case ContentExtractionDialogFields.FROM_TEXT:
				parameter.links!.text!.fromText = value;
				break;
			case ContentExtractionDialogFields.REMOVE_PUNCTUATION:
				parameter.words!.removePunctuation = value;
				break;
			case ContentExtractionDialogFields.DELIMIT_AFTER_PUNCTUATION:
				parameter.words!.delimitAfterPunctuation = value;
				break;
			case ContentExtractionDialogFields.EXTENDED_SEQUENCE_CHARACTERS:
				parameter.words!.extendedSequenceCharacters = value;
				break;
			case ContentExtractionDialogFields.FALLBACK_FORMAT:
				parameter.images!.fallbackFormat = value;
				break;
			case ContentExtractionDialogFields.PAGERANGE:
				let checkPagerange: boolean = true;

				try {
					PageSelection.fromString(value, this.state.pages);
				} catch (e: any) {
					checkPagerange = false;
				}

				if (!FunctionHelper.exists(value) || FunctionHelper.isEmpty(value)) {
					errors.set(field, "errors.fields.set");
				} else if (!checkPagerange) {
					errors.set(field, "errors.fields.pagerange");
				} else if (errors.has(field)) {
					errors.delete(field);
				}

				parameter.text!.pages = value;
				parameter.images!.pages = value;
				parameter.links!.pages = value;
				parameter.words!.pages = value;
				break;
		}

		this.setState({
			parameter: this.state.parameter
		});
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-export-content small"}/>
					<Translate {...this.props} data="extract_content.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	protected isEditPasswordNeeded(portalDocument: PortalDocument): boolean {
		return super.isEditPasswordNeeded(portalDocument) && !FunctionHelper.applyValue(
			portalDocument.metadata?.security?.canExtractContent, Security.getCanExtractContentDefault()
		);
	}

	/**
	 * Handle showing/initializing the dialog
	 */
	protected async onShow(): Promise<void> {
		await super.onShow();

		let parameter: PortalToolboxExtractionExtraction | undefined = this.state.parameter?.data;
		let portalDocument: PortalDocument = this.props.context.documentManager.getSelection()[0];
		let documentSettings: DocumentSettings = portalDocument.getSettings();
		let currentPage: string = documentSettings.activeViewer.currentPage.toString();
		let pageCount: number = await portalDocument.getNumberOfPages();

		if (!FunctionHelper.exists(parameter)) {
			return;
		}

		let pageSelection: string = documentSettings.pageSelection.toString(
			pageCount, documentSettings.activeViewer.currentPage
		);
		parameter!.text!.pages = pageSelection;
		parameter!.images!.pages = pageSelection;
		parameter!.links!.pages = pageSelection;
		parameter!.words!.pages = pageSelection;

		this.setState({
			parameter: this.state.parameter,
			defaultParameter: this.state.parameter?.clone(),
			pages: pageCount,
			currentPage: currentPage
		});
	};

	protected async submit(): Promise<void> {
		await super.submit();

		let contentExtractionParameter: PortalToolboxExtractionExtraction = this.state.parameter!.data.clone();
		switch (contentExtractionParameter.mode) {
			case ExtractionMode.TEXT:
				contentExtractionParameter.images = undefined;
				contentExtractionParameter.words = undefined;
				contentExtractionParameter.links = undefined;
				break;
			case ExtractionMode.WORDS:
				contentExtractionParameter.images = undefined;
				contentExtractionParameter.text = undefined;
				contentExtractionParameter.links = undefined;
				break;
			case ExtractionMode.URL:
				contentExtractionParameter.text = undefined;
				contentExtractionParameter.images = undefined;
				contentExtractionParameter.words = undefined;
				break;
			case ExtractionMode.IMAGES:
				contentExtractionParameter.text = undefined;
				contentExtractionParameter.words = undefined;
				contentExtractionParameter.links = undefined;
				break;
		}

		let toolboxOperation: BaseToolbox = BaseToolbox.fromJson({
			extraction: contentExtractionParameter.toJson()
		});

		let operationData: Array<BaseToolbox> = [toolboxOperation];
		let webServiceType: WebServiceType = WebServiceTypes.TOOLBOX;
		let documentManager: PortalDocumentManager = this.props.context.documentManager;
		let selection: Array<PortalDocument> = documentManager.getSelection();

		for (let portalDocument of selection) {
			try {
				await this.props.context.session.executeRequest({
					webServiceType: webServiceType,
					document: portalDocument,
					operation: operationData,
					settings: Settings.fromJson({
						pdfa: {
							pdfaEditProfile: this.props.pdfaEditProfile
						},
						signature: {
							signatureEditProfile: this.props.signatureEditProfile
						},
						compress: {
							compressProfile: this.props.compressProfile,
							compressObjects: {
								objectStreamSize: this.props.objectStreamSize
							}
						}
					} as SettingsInterface)
				});
			} catch (exception: any) {
				if (await this.onError(exception)) {
					await this.submit();
				}
				return;
			}

			let message: Message = new Message("success.toolbox.extract.content", MessageType.SUCCESS);
			message.context = {filename: '"' + portalDocument.getFileName() + '" '};
			this.props.context.getPage().addMessage(message);
			this.hide();
		}
	}

	protected getDialogType(): PortalDialogType {
		return PortalDialogType.EXTRACT_CONTENT;
	}

	protected getDefaultParameter(): Parameter<PortalToolboxExtractionExtraction> {
		return DialogDefault.getParameter(
			this.getDialogType(), this.props.context.documentManager.getSelection()
		);
	}
}

const ContentExtractionDialog = React.memo(ContentExtractionDialogDefinition,
	(
		prevProps: Readonly<PortalDialogProperties<PortalToolboxExtractionExtraction>>,
		nextProps: Readonly<PortalDialogProperties<PortalToolboxExtractionExtraction>>
	) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default ContentExtractionDialog;